import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftRight, Store, Building2, CircleCheck, CircleAlert, Loader2, Settings2, ArrowLeft } from 'lucide-react';
import { useCompany } from '@/context/CompanyContext';
import { usePermissions } from '@/hooks/usePermissions';
import { emitToast } from '@/components/ui/Toast';
import { ERP_PROVIDERS, STORE_PROVIDERS, type ProviderDef } from '@/lib/interfacesCatalog';
import { findIntegration, interfacesService, type ConnectionPatch, type IntegrationSummary } from '@/lib/interfacesService';
import { useInterfaceConnection } from './useInterfaceConnection';
import { ConnectionIdentityCard } from '@/components/integrations/ConnectionIdentityCard';

interface ProviderTileProps {
  companyId: string;
  provider: ProviderDef<string>;
  integration: IntegrationSummary | undefined;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}

function ProviderTile({ companyId, provider, integration, selected, disabled, onSelect }: ProviderTileProps) {
  const connected = !!integration?.is_active;

  if (!provider.available) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 opacity-70">
        <div className="flex items-center justify-between gap-2">
          <p className="font-semibold text-gray-700">{provider.label}</p>
          <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600">בקרוב</span>
        </div>
        <p className="mt-1 text-sm text-gray-500">{provider.description}</p>
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border-2 p-4 transition-all duration-200 ${
        selected ? 'border-sky-500 bg-sky-50 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold text-gray-900">{provider.label}</p>
        {connected ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
            <CircleCheck className="h-3.5 w-3.5" /> מחובר
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
            <CircleAlert className="h-3.5 w-3.5" /> לא מחובר
          </span>
        )}
      </div>
      <p className="mt-1 text-sm text-gray-500">{provider.description}</p>

      {connected && integration && (
        <div className="mt-3">
          <ConnectionIdentityCard
            companyId={companyId}
            integrationId={integration.id}
            providerName={integration.provider_name}
            variant="compact"
          />
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {connected ? (
          <button
            type="button"
            onClick={onSelect}
            disabled={disabled || selected}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              selected
                ? 'bg-sky-600 text-white cursor-default'
                : 'bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50'
            }`}
          >
            {selected ? 'נבחר' : 'בחר'}
          </button>
        ) : (
          <Link
            to={provider.settingsPath}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Settings2 className="h-4 w-4" /> הגדר חיבור
          </Link>
        )}
      </div>
    </div>
  );
}

export function InterfacesSetupPage() {
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const { can } = usePermissions();
  const canEdit = can({ module: 'interfaces', level: 'editor' });
  const { connection, setConnection, integrations, loading, error } = useInterfaceConnection(companyId);
  const [saving, setSaving] = useState(false);

  const save = async (patch: ConnectionPatch, successMsg: string) => {
    if (!companyId) return;
    setSaving(true);
    try {
      const saved = await interfacesService.saveConnection(companyId, patch);
      setConnection(saved);
      emitToast(successMsg, 'success');
    } catch (e) {
      console.error('Failed to save interface connection:', e);
      emitToast('השמירה נכשלה, נסו שוב', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-sky-600" /></div>;
  }
  if (error) {
    return <div className="mx-auto max-w-3xl rounded-xl border border-red-200 bg-red-50 p-6 text-red-700">{error}</div>;
  }

  const bothSelected = !!connection?.store_provider && !!connection?.erp_provider;

  return (
    <div className="mx-auto max-w-5xl space-y-8" dir="rtl">
      <header>
        <h1 className="text-2xl font-bold text-gray-900">חיבור המערכות</h1>
        <p className="mt-1 text-gray-600">בחרו את החנות שלכם ואת מערכת הניהול, ואז הפעילו את הממשקים הרצויים ביניהן.</p>
      </header>

      {!canEdit && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
          יש לכם הרשאת צפייה בלבד. לשינוי ההגדרות פנו למנהל הממשקים בחברה.
        </div>
      )}

      <section className="grid gap-6 lg:grid-cols-[1fr_auto_1fr] lg:items-start">
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <Store className="h-5 w-5 text-sky-600" />
            <h2 className="text-lg font-semibold text-gray-900">האתר שלי</h2>
          </div>
          <div className="space-y-3">
            {STORE_PROVIDERS.map(p => {
              const integration = findIntegration(integrations, p.integrationProviders);
              return (
                <ProviderTile
                  key={p.key}
                  companyId={companyId!}
                  provider={p}
                  integration={integration}
                  selected={connection?.store_provider === p.key}
                  disabled={!canEdit || saving}
                  onSelect={() => save(
                    { store_provider: p.key, store_integration_id: integration?.id ?? null },
                    `${p.label} נבחר כאתר`,
                  )}
                />
              );
            })}
          </div>
        </div>

        <div className="hidden lg:flex h-full items-center justify-center pt-16">
          <div className={`rounded-full p-3 transition-colors ${bothSelected ? 'bg-sky-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
            <ArrowLeftRight className="h-6 w-6" />
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-sky-600" />
            <h2 className="text-lg font-semibold text-gray-900">מערכת הניהול שלי</h2>
          </div>
          <div className="space-y-3">
            {ERP_PROVIDERS.map(p => {
              const integration = findIntegration(integrations, p.integrationProviders);
              return (
                <ProviderTile
                  key={p.key}
                  companyId={companyId!}
                  provider={p}
                  integration={integration}
                  selected={connection?.erp_provider === p.key}
                  disabled={!canEdit || saving}
                  onSelect={() => save(
                    { erp_provider: p.key, erp_integration_id: integration?.id ?? null },
                    `${p.label} נבחרה כמערכת הניהול`,
                  )}
                />
              );
            })}
            <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
              מערכות ניהול נוספות יתווספו בהמשך
            </div>
          </div>
        </div>
      </section>

      {bothSelected && (
        <Link
          to="/app/interfaces/flows"
          className="flex items-center justify-between rounded-2xl bg-sky-600 px-6 py-4 text-white shadow-sm transition-colors hover:bg-sky-700"
        >
          <span className="font-semibold">שני הצדדים מחוברים - המשיכו לבחירת הממשקים</span>
          <ArrowLeft className="h-5 w-5" />
        </Link>
      )}

      <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">שמירת יומן פעולות</h2>
        <p className="mt-1 text-sm text-gray-600">המערכת לא שומרת עותק של ההזמנות, רק תיעוד של מה שהועבר.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">מה לתעד</span>
            <select
              value={connection?.log_mode ?? 'all'}
              disabled={!canEdit || saving}
              onChange={e => save({ log_mode: e.target.value as 'all' | 'errors_only' }, 'הגדרת היומן נשמרה')}
              className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-gray-50"
            >
              <option value="all">את כל הפעולות</option>
              <option value="errors_only">רק כשלונות</option>
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">כמה זמן לשמור</span>
            <select
              value={connection?.log_retention_days ?? 90}
              disabled={!canEdit || saving}
              onChange={e => save({ log_retention_days: Number(e.target.value) as 30 | 90 | 365 }, 'הגדרת היומן נשמרה')}
              className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-gray-50"
            >
              <option value={30}>30 יום</option>
              <option value={90}>90 יום</option>
              <option value={365}>שנה</option>
            </select>
          </label>
        </div>
      </section>
    </div>
  );
}
